import { memo, useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useBankAccountStore } from '@/stores/bankAccountStore';
import { useTransactionStore } from '@/stores/transactionStore';
import { apiService } from '@/services/api';
import { useBankLogo } from '@/hooks/useBankLogo';
import { SEARCH_DEBOUNCE_MS, useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { TransactionDetailPanel } from '@/components/TransactionDetailPanel';
import { TransactionListFooter } from '@/components/TransactionListFooter';
import type { BankAccountDeletePreview, Transaction, TransactionSearchFilter, UpdateBankAccountRequest } from '@/types';
import Icon from '../components/Icon';
import BankLogo from '../components/BankLogo';

// Consent status helper function
const getConsentStatus = (consentExpiresAt: string) => {
  if (!consentExpiresAt || consentExpiresAt === '0001-01-01T00:00:00' || consentExpiresAt.startsWith('0001-01-01')) {
    return { state: 'missing' as const, daysLeft: 0, expiryDate: null };
  }
  const now = new Date();
  const expires = new Date(consentExpiresAt);

  if (isNaN(expires.getTime())) {
    return { state: 'missing' as const, daysLeft: 0, expiryDate: null };
  }

  const daysLeft = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (daysLeft <= 0) return { state: 'expired' as const, daysLeft: 0, expiryDate: expires };
  if (daysLeft <= 30) return { state: 'expiring' as const, daysLeft, expiryDate: expires };
  return { state: 'valid' as const, daysLeft, expiryDate: expires };
};

const formatCurrency = (amount: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

// Memoised: appending a page renders only its own rows, not the thousands already shown.
const TransactionRow = memo(function TransactionRow({
  transaction,
  onSelect,
}: {
  transaction: Transaction;
  onSelect: (transaction: Transaction) => void;
}) {
  return (
    <tr
      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
      onClick={() => onSelect(transaction)}
      data-testid={`transaction-row-${transaction.id}`}
    >
      <td className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
              transaction.amount < 0
                ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
            }`}
          >
            <Icon name={transaction.amount < 0 ? 'arrow_upward' : 'arrow_downward'} />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-slate-900 dark:text-white [overflow-wrap:anywhere]">{transaction.description}</span>
            {transaction.purposeCode && (
              <span className="text-xs text-slate-400 dark:text-slate-500">{transaction.purposeCode}</span>
            )}
            {/* Phone: the date column is hidden, so the date sits under the description */}
            <span className="sm:hidden text-xs text-slate-500 dark:text-slate-400">{formatDate(transaction.transactionDate)}</span>
          </div>
        </div>
      </td>
      <td className="hidden sm:table-cell px-6 py-4 text-sm text-slate-600 dark:text-slate-400">
        {formatDate(transaction.transactionDate)}
      </td>
      <td
        className={`px-3 sm:px-6 py-3 sm:py-4 text-right font-semibold whitespace-nowrap ${
          transaction.amount < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
        }`}
      >
        {transaction.amount > 0 ? '+' : ''}
        {formatCurrency(transaction.amount, transaction.currency)}
      </td>
      <td className="hidden sm:table-cell px-6 py-4 text-right text-sm text-slate-600 dark:text-slate-400">
        {formatCurrency(transaction.runningBalance, transaction.currency)}
      </td>
    </tr>
  );
});

export const AccountDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const logoFor = useBankLogo();
  const { currentAccount, accounts, fetchAccounts, fetchAccountById, setCurrentAccount, removeConsent, initiateConsent, deleteAccount, getDeletePreview, updateAccount, isLoading: accountLoading, clearError: clearAccountError } = useBankAccountStore();
  const {
    transactions,
    isLoading,
    isLoadingMore,
    loadMoreError,
    hasMore,
    totalCount,
    error,
    fetchTransactions,
    loadMoreTransactions,
    setSearchFilters,
    clearError,
    reset: resetTransactions,
  } = useTransactionStore();

  const [searchString, setSearchString] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showAmountFilter, setShowAmountFilter] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showRemoveConsentModal, setShowRemoveConsentModal] = useState(false);
  const [isRemovingConsent, setIsRemovingConsent] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleted, setIsDeleted] = useState(false);
  // Delete dialog: the owner chooses whether the movements go too (default: keep them).
  const [deleteMovements, setDeleteMovements] = useState(false);
  const [deletePreview, setDeletePreview] = useState<BankAccountDeletePreview | null>(null);
  // Edit account (name + LusoPay write-only credentials)
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState('');
  const [editGuid, setEditGuid] = useState('');
  const [editTax, setEditTax] = useState('');
  const [removeCreds, setRemoveCreds] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidatingCreds, setIsValidatingCreds] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Parse the route parameter to number
  const accountId = id ? Number(id) : null;

  // Memoized function to load account and transactions
  const loadAccountData = useCallback(async () => {
    // Skip if account was deleted or ID is invalid
    if (!accountId || isDeleted) return;

    // Reset transactions when switching accounts
    resetTransactions();

    // Try to find account in local store first
    const localAccount = accounts.find((a) => a.id === accountId);
    if (localAccount) {
      setCurrentAccount(localAccount);
    } else {
      await fetchAccountById(accountId);
    }

    // Always load transactions from database
    await fetchTransactions(accountId);
  }, [
    accountId,
    accounts,
    fetchAccountById,
    fetchTransactions,
    resetTransactions,
    setCurrentAccount,
    isDeleted,
  ]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  // Clear errors when unmounting
  useEffect(() => {
    return () => {
      clearAccountError();
    };
  }, [clearAccountError]);

  // Every control sends the complete filter set: the store replaces searchFilters wholesale,
  // so passing only one control's keys silently drops the others. Pass `overrides` for a value
  // whose state was just set in this same handler — that setState has not been applied yet.
  const buildFilters = useCallback(
    (overrides: TransactionSearchFilter = {}): TransactionSearchFilter => {
      const filters: TransactionSearchFilter = {};
      if (searchString) filters.searchString = searchString;
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (minAmount) filters.minAmount = parseFloat(minAmount);
      if (maxAmount) filters.maxAmount = parseFloat(maxAmount);
      return { ...filters, ...overrides };
    },
    [searchString, startDate, endDate, minAmount, maxAmount]
  );

  const applyFilters = useCallback(
    async (filters: TransactionSearchFilter) => {
      setSearchFilters(filters);
      if (accountId) {
        await fetchTransactions(accountId);
      }
    },
    [accountId, fetchTransactions, setSearchFilters]
  );

  // The search has to go back to the database: the table shows one page of results, not the
  // account's whole history, so filtering the rows already loaded would hide real matches.
  // It queries once typing pauses, with the filters as they are then.
  const searchWhenTypingStops = useDebouncedCallback(() => {
    applyFilters(buildFilters());
  }, SEARCH_DEBOUNCE_MS);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
    searchWhenTypingStops();
  };

  const handleDateFilterApply = async () => {
    await applyFilters(buildFilters());
    setShowDateFilter(false);
  };

  const handleDateFilterClear = async () => {
    setStartDate('');
    setEndDate('');
    await applyFilters(buildFilters({ startDate: undefined, endDate: undefined }));
    setShowDateFilter(false);
  };

  const handleAmountFilterApply = async () => {
    await applyFilters(buildFilters());
    setShowAmountFilter(false);
  };

  const handleAmountFilterClear = async () => {
    setMinAmount('');
    setMaxAmount('');
    await applyFilters(buildFilters({ minAmount: undefined, maxAmount: undefined }));
    setShowAmountFilter(false);
  };

  const handleRemoveConsent = async () => {
    if (!accountId) return;

    setIsRemovingConsent(true);
    try {
      await removeConsent(accountId);
      setShowRemoveConsentModal(false);
    } catch {
      // Error is handled by the store
    } finally {
      setIsRemovingConsent(false);
    }
  };

  const handleProvideConsent = async () => {
    if (!accountId) return;

    const redirectUrl = await initiateConsent(accountId);
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  // Opening the delete dialog loads how many movements the account holds and how many are
  // reconciled with documents; the dialog works without them (null) if that call fails.
  useEffect(() => {
    if (!showDeleteModal || !accountId) return;
    let cancelled = false;
    setDeleteMovements(false);
    setDeletePreview(null);
    getDeletePreview?.(accountId).then((preview) => {
      if (!cancelled) setDeletePreview(preview ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [showDeleteModal, accountId, getDeletePreview]);

  const handleDeleteAccount = async () => {
    if (!accountId) return;

    setIsDeleting(true);
    setDeleteError(null);
    // Set deleted flag BEFORE calling deleteAccount to prevent re-fetching
    setIsDeleted(true);

    try {
      await deleteAccount(accountId, deleteMovements);
      setShowDeleteModal(false);
      clearAccountError(); // Clear any errors from the store
      navigate('/');
    } catch (err) {
      // Reset deleted flag if delete failed
      setIsDeleted(false);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenEdit = () => {
    if (!currentAccount) return;
    setEditName(currentAccount.friendlyName ?? '');
    setEditGuid('');
    setEditTax('');
    setRemoveCreds(false);
    setEditError(null);
    setShowEditModal(true);
  };

  // Arriving from the Add flow for a freshly-created LusoPay account (?setup=lusopay):
  // auto-open the credentials modal so the user can enter their merchant credentials.
  const lusopaySetupHandled = useRef(false);
  useEffect(() => {
    if (lusopaySetupHandled.current) return;
    if (
      currentAccount &&
      currentAccount.integrationType === 'LusoPay' &&
      searchParams.get('setup') === 'lusopay'
    ) {
      lusopaySetupHandled.current = true;
      handleOpenEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount, searchParams]);

  const handleSaveEdit = async () => {
    if (!accountId || !currentAccount) return;
    setEditError(null);

    const isLusopay = currentAccount.integrationType === 'LusoPay';
    const guid = editGuid.trim();
    const tax = editTax.trim();

    // Credentials are set as a pair; reject a half-filled entry.
    if (isLusopay && !removeCreds && (guid || tax) && (!guid || !tax)) {
      setEditError('Enter both the LusoPay GUID and the tax number, or leave both blank.');
      return;
    }

    // Catch format mistakes (most commonly the two values swapped) before submitting.
    if (isLusopay && !removeCreds && guid && tax) {
      const guidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidPattern.test(guid)) {
        setEditError(
          /^\d{9}$/.test(guid)
            ? 'The first field must be the LusoPay GUID — it looks like the tax number was entered there instead.'
            : 'The LusoPay GUID must be a valid GUID (36 characters, 8-4-4-4-12 with dashes).',
        );
        return;
      }
      if (!/^\d{9}$/.test(tax)) {
        setEditError('The tax number (NIF) must be exactly 9 digits.');
        return;
      }
    }

    const request: UpdateBankAccountRequest = {};
    const name = editName.trim();
    if (name && name !== currentAccount.friendlyName) request.friendlyName = name;
    if (isLusopay) {
      if (removeCreds) request.clearLusopayCredentials = true;
      else if (guid && tax) {
        request.lusopayGuid = guid;
        request.lusopayTaxNumber = tax;
      }
    }

    const credsWereSet = isLusopay && !removeCreds && !!guid && !!tax;

    setIsSaving(true);
    try {
      await updateAccount(accountId, request);

      if (credsWereSet) {
        // Sync immediately — its outcome is the credential confirmation. On failure the
        // credentials stay stored, the modal stays open, and the user can re-enter them.
        setIsValidatingCreds(true);
        try {
          await apiService.syncAccount(accountId);
          await fetchAccounts(); // refresh balance/status; the page picks up the new data
        } catch (syncErr) {
          setEditError(syncErr instanceof Error ? syncErr.message : 'Credentials saved, but the first sync failed. Please re-check them.');
          return;
        } finally {
          setIsValidatingCreds(false);
        }
      }

      setShowEditModal(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save changes.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadMore = useCallback(() => {
    loadMoreTransactions();
  }, [loadMoreTransactions]);

  const formatRelativeDate = (dateString: string) => {
    // Handle null, empty, or C# default DateTime (0001-01-01)
    if (!dateString || dateString === '0001-01-01T00:00:00' || dateString.startsWith('0001-01-01')) {
      return 'Never';
    }

    const date = new Date(dateString);

    // Handle invalid dates
    if (isNaN(date.getTime())) {
      return 'Never';
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) {
      return 'Just now';
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    }
  };

  if (!currentAccount) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-400">
          <Icon name="progress_activity" className="animate-spin" />
          Loading account details...
        </div>
      </div>
    );
  }

  const consentStatus = getConsentStatus(currentAccount.consentExpiresAt);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Link */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
          >
            <Icon name="arrow_back" />
            <span>Back to Accounts</span>
          </Link>
        </div>

        {/* Account Header */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 mb-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <BankLogo
                {...logoFor(currentAccount)}
                name={currentAccount.bankName}
                className="h-14 w-14 p-2 rounded-xl border border-slate-200 dark:border-slate-700"
              />
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">
                    {currentAccount.friendlyName}
                  </h1>
                  <button
                    onClick={handleOpenEdit}
                    className="p-1.5 text-slate-400 hover:text-primary dark:hover:text-primary transition-colors"
                    title="Edit Account"
                    data-testid="edit-account-button"
                  >
                    <Icon name="edit" size={22} />
                  </button>
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="p-1.5 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                    title="Delete Account"
                    data-testid="delete-account-button"
                  >
                    <Icon name="delete" size={22} />
                  </button>
                </div>
                <p className="text-slate-500 dark:text-slate-400 mt-1">{currentAccount.bankName}</p>
                {currentAccount.type === 'Credit' ? (
                  <p className="flex items-center gap-2 font-mono text-sm text-slate-400 dark:text-slate-500 mt-1">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 not-italic">
                      <Icon name="credit_card" size={14} />
                      Credit card
                    </span>
                    {currentAccount.maskedPan || '•••• ••••'}
                  </p>
                ) : (
                  <p className="font-mono text-sm text-slate-400 dark:text-slate-500 mt-1">{currentAccount.iban}</p>
                )}
                {/* Connection status — LusoPay shows credentials state (non-expiring); others show consent */}
                <div className="flex items-center gap-2 mt-2">
                  {currentAccount.integrationType === 'LusoPay' ? (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          currentAccount.lusopayCredentialsSet
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                        }`}
                      >
                        <Icon name={currentAccount.lusopayCredentialsSet ? 'check_circle' : 'key'} size={16} />
                        {currentAccount.lusopayCredentialsSet ? 'Credentials set' : 'Credentials needed'}
                      </span>
                      <button onClick={handleOpenEdit} className="text-xs text-primary hover:underline">
                        {currentAccount.lusopayCredentialsSet ? 'Update' : 'Add credentials'}
                      </button>
                    </>
                  ) : (
                    <>
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          consentStatus.state === 'valid'
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : consentStatus.state === 'expiring'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : consentStatus.state === 'expired'
                            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                        }`}
                      >
                        <Icon name={consentStatus.state === 'valid' ? 'check_circle' : consentStatus.state === 'expiring' ? 'schedule' : 'cancel'} size={16} />
                        {consentStatus.state === 'valid'
                          ? `Consent: ${consentStatus.daysLeft}d`
                          : consentStatus.state === 'expiring'
                          ? `Expiring: ${consentStatus.daysLeft}d`
                          : consentStatus.state === 'expired'
                          ? 'Expired'
                          : 'No Consent'}
                      </span>
                      {(consentStatus.state === 'valid' || consentStatus.state === 'expiring') && (
                        <button
                          onClick={() => setShowRemoveConsentModal(true)}
                          className="p-0.5 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                          title="Remove Consent"
                        >
                          <Icon name="close" size={16} />
                        </button>
                      )}
                      {(currentAccount.integrationType === 'Sibs' || currentAccount.integrationType === 'Revolut') && (consentStatus.state === 'missing' || consentStatus.state === 'expired') && (
                        <button
                          onClick={handleProvideConsent}
                          disabled={accountLoading}
                          className="text-xs text-primary hover:underline disabled:opacity-50"
                        >
                          Provide Consent
                        </button>
                      )}
                      {(currentAccount.integrationType === 'Sibs' || currentAccount.integrationType === 'Revolut') && consentStatus.state === 'expiring' && (
                        <button
                          onClick={handleProvideConsent}
                          disabled={accountLoading}
                          className="text-xs text-amber-600 dark:text-amber-400 hover:underline disabled:opacity-50"
                        >
                          Renew
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500 dark:text-slate-400">Current Balance</p>
              <p
                className={`text-3xl font-bold ${
                  currentAccount.balance < 0 ? 'text-red-500 dark:text-red-400' : 'text-slate-900 dark:text-white'
                }`}
              >
                {formatCurrency(currentAccount.balance, currentAccount.currency)}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Last synced {formatRelativeDate(currentAccount.lastSyncedAt)}
              </p>
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Icon name="error" className="text-red-600 dark:text-red-400" />
              <span className="text-red-800 dark:text-red-200">{error}</span>
            </div>
            <button
              onClick={clearError}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              <Icon name="close" />
            </button>
          </div>
        )}

        {/* Transactions Section */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
          {/* Filters Header */}
          <div className="p-4 border-b border-slate-200 dark:border-slate-800 relative z-30">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <input
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 h-10 pl-10 pr-4 text-sm focus:ring-primary focus:border-primary dark:text-slate-200"
                  placeholder="Search transactions..."
                  type="text"
                  value={searchString}
                  onChange={handleSearch}
                />
                <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
              </div>

              {/* Date Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowDateFilter(!showDateFilter);
                    setShowAmountFilter(false);
                  }}
                  className={`flex items-center gap-2 rounded-lg h-10 px-4 text-sm font-medium border transition-colors ${
                    startDate || endDate
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-primary'
                  }`}
                >
                  <Icon name="calendar_today" />
                  <span>Date</span>
                  {(startDate || endDate) && (
                    <span className="w-2 h-2 rounded-full bg-white"></span>
                  )}
                </button>
                {showDateFilter && (
                  <div className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-4 z-40">
                    <div className="flex flex-col gap-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white">Date Range</h3>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="start-date">
                          Start Date
                        </label>
                        <input
                          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="end-date">
                          End Date
                        </label>
                        <input
                          className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={handleDateFilterClear}
                          className="rounded-md h-9 px-4 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleDateFilterApply}
                          className="rounded-md h-9 px-4 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Amount Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowAmountFilter(!showAmountFilter);
                    setShowDateFilter(false);
                  }}
                  className={`flex items-center gap-2 rounded-lg h-10 px-4 text-sm font-medium border transition-colors ${
                    minAmount || maxAmount
                      ? 'bg-primary text-white border-primary'
                      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700 hover:border-primary'
                  }`}
                >
                  <Icon name="payments" />
                  <span>Amount</span>
                  {(minAmount || maxAmount) && (
                    <span className="w-2 h-2 rounded-full bg-white"></span>
                  )}
                </button>
                {showAmountFilter && (
                  <div className="absolute left-0 top-full mt-2 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg p-4 z-40">
                    <div className="flex flex-col gap-4">
                      <h3 className="font-semibold text-slate-900 dark:text-white">Amount Range</h3>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="min-amount">
                            Min
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                            id="min-amount"
                            placeholder="0"
                            type="number"
                            value={minAmount}
                            onChange={(e) => setMinAmount(e.target.value)}
                          />
                        </div>
                        <span className="text-slate-400 mt-5">-</span>
                        <div className="flex-1">
                          <label className="text-sm font-medium text-slate-600 dark:text-slate-400" htmlFor="max-amount">
                            Max
                          </label>
                          <input
                            className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                            id="max-amount"
                            placeholder="10000"
                            type="number"
                            value={maxAmount}
                            onChange={(e) => setMaxAmount(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                        <button
                          onClick={handleAmountFilterClear}
                          className="rounded-md h-9 px-4 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                          Clear
                        </button>
                        <button
                          onClick={handleAmountFilterApply}
                          className="rounded-md h-9 px-4 text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Description
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Amount
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center gap-3 text-slate-500 dark:text-slate-400">
                        <Icon name="progress_activity" className="animate-spin" />
                        Loading transactions...
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Icon name="receipt_long" size={48} className="text-slate-300 dark:text-slate-600" />
                        <p className="text-slate-500 dark:text-slate-400">No transactions found for this account.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <TransactionRow key={transaction.id} transaction={transaction} onSelect={setSelectedTransaction} />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Infinite scroll: next page, loading, end of list */}
          {!isLoading && (
            <TransactionListFooter
              loadedCount={transactions.length}
              totalCount={totalCount}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              loadMoreError={loadMoreError}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      </div>

      {/* Click outside to close filters */}
      {(showDateFilter || showAmountFilter) && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => {
            setShowDateFilter(false);
            setShowAmountFilter(false);
          }}
        />
      )}

      {/* Transaction Detail Panel */}
      {selectedTransaction && (
        <TransactionDetailPanel
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}

      {/* Remove Consent Confirmation Modal */}
      {showRemoveConsentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !isRemovingConsent && setShowRemoveConsentModal(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Icon name="warning" size={26} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Remove Consent</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">This action cannot be undone</p>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-slate-700 dark:text-slate-300 mb-3">
                Are you sure you want to remove the consent for this account?
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="info" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    <p className="font-medium mb-1">Without consent:</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700 dark:text-amber-300">
                      <li>Transaction synchronization will stop</li>
                      <li>Balance updates will no longer be available</li>
                      <li>You will need to provide consent again to resume</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRemoveConsentModal(false)}
                disabled={isRemovingConsent}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveConsent}
                disabled={isRemovingConsent}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isRemovingConsent ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    Removing...
                  </>
                ) : (
                  <>
                    <Icon name="delete" />
                    Remove Consent
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditModal && currentAccount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !isSaving && setShowEditModal(false)}
          />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="edit" className="text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Edit Account</h2>
            </div>

            {/* Account name */}
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1" htmlFor="edit-name">
              Account name
            </label>
            <input
              id="edit-name"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
              placeholder="Account name"
            />

            {/* LusoPay credentials — write-only, never prefilled from the server/DB */}
            {currentAccount.integrationType === 'LusoPay' && (
              <div className="mt-5 border-t border-slate-200 dark:border-slate-700 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">LusoPay credentials</span>
                  {currentAccount.lusopayCredentialsSet && !removeCreds && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-300">
                      <Icon name="verified_user" size={14} />
                      Configured
                      <button
                        type="button"
                        onClick={() => setRemoveCreds(true)}
                        title="Remove credentials"
                        className="ml-1 p-0.5 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                      >
                        <Icon name="close" size={14} />
                      </button>
                    </span>
                  )}
                </div>

                {removeCreds ? (
                  <div className="flex items-center justify-between text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-2">
                    <span>Credentials will be removed on save.</span>
                    <button type="button" onClick={() => setRemoveCreds(false)} className="text-xs underline">Undo</button>
                  </div>
                ) : (
                  <>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      LusoPay GUID
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={editGuid}
                      onChange={(e) => setEditGuid(e.target.value)}
                      className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary mb-2"
                      placeholder={currentAccount.lusopayCredentialsSet ? '•••••••• (leave blank to keep)' : 'e.g. 12345678-abcd-…'}
                    />
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Tax number (NIF)
                    </label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      value={editTax}
                      onChange={(e) => setEditTax(e.target.value)}
                      className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 p-2 text-sm dark:text-slate-200 focus:ring-primary focus:border-primary"
                      placeholder={currentAccount.lusopayCredentialsSet ? '•••••••• (leave blank to keep)' : '9 digits'}
                    />
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Stored encrypted — never shown again.</p>
                  </>
                )}
              </div>
            )}

            {editError && (
              <div className="mt-4 text-sm text-red-600 dark:text-red-400">{editError}</div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                disabled={isSaving}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isValidatingCreds ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    Validating credentials…
                  </>
                ) : isSaving ? (
                  <>
                    <Icon name="progress_activity" className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <>
          {/* Modal Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
            data-testid="delete-modal-backdrop"
          />

          {/* Modal Content */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            data-testid="delete-account-modal"
          >
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full p-6">
              {/* Modal Header */}
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <Icon name="warning" size={26} className="text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Delete Account</h2>
                  <p className="text-sm text-slate-500 dark:text-slate-400">This action cannot be undone</p>
                </div>
              </div>

              {/* Modal Body */}
              <div className="mb-6">
                <p className="text-slate-700 dark:text-slate-300 mb-4">
                  Are you sure you want to delete <span className="font-semibold">{currentAccount.friendlyName}</span>?
                  The account is removed and its bank consent revoked.
                </p>

                {/* Movements: keep (default) or delete with the account */}
                <fieldset className="space-y-2" disabled={isDeleting}>
                  <legend className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                    {deletePreview
                      ? `What should happen to its ${deletePreview.movementCount} ${deletePreview.movementCount === 1 ? 'movement' : 'movements'}?`
                      : 'What should happen to its movements?'}
                  </legend>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${!deleteMovements
                    ? 'border-slate-400 dark:border-slate-500 bg-slate-50 dark:bg-slate-800'
                    : 'border-slate-200 dark:border-slate-700'}`}>
                    <input
                      type="radio"
                      name="delete-movements"
                      className="mt-1"
                      checked={!deleteMovements}
                      onChange={() => setDeleteMovements(false)}
                      data-testid="delete-movements-keep"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      <span className="font-medium text-slate-900 dark:text-white block">Keep the movements</span>
                      They stay in your records, with any reconciliation they already have.
                    </span>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${deleteMovements
                    ? 'border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                    : 'border-slate-200 dark:border-slate-700'}`}>
                    <input
                      type="radio"
                      name="delete-movements"
                      className="mt-1"
                      checked={deleteMovements}
                      onChange={() => setDeleteMovements(true)}
                      data-testid="delete-movements-delete"
                    />
                    <span className="text-sm text-slate-700 dark:text-slate-300">
                      <span className="font-medium text-slate-900 dark:text-white block">
                        {deletePreview
                          ? `Also delete its ${deletePreview.movementCount} ${deletePreview.movementCount === 1 ? 'movement' : 'movements'}`
                          : 'Also delete its movements'}
                      </span>
                      They are removed from your records together with the account.
                    </span>
                  </label>
                </fieldset>

                {/* Reconciliation warning: deleting reconciled movements removes those reconciliations */}
                {deletePreview && deletePreview.reconciledCount > 0 && (
                  <div
                    className={`mt-4 rounded-lg p-4 border ${deleteMovements
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                      : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'}`}
                    data-testid="delete-reconciled-warning"
                  >
                    <div className="flex items-start gap-3">
                      <Icon name="warning" className={deleteMovements ? 'text-red-600 dark:text-red-400 mt-0.5' : 'text-amber-600 dark:text-amber-400 mt-0.5'} />
                      <div className={`text-sm ${deleteMovements ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'}`}>
                        <p className="font-medium mb-1">
                          {deletePreview.reconciledCount} of {deletePreview.movementCount === 1 ? 'this movement is' : 'these movements are'} reconciled with documents
                        </p>
                        <p>
                          {deleteMovements
                            ? 'Deleting the movements removes those reconciliations.'
                            : 'Keeping the movements keeps those reconciliations. Deleting them would remove them.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Error */}
                {deleteError && (
                  <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center gap-2">
                    <Icon name="error" className="text-red-600 dark:text-red-400" />
                    <span className="text-sm text-red-800 dark:text-red-200" data-testid="delete-error-message">{deleteError}</span>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="delete-modal-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  data-testid="delete-modal-confirm"
                >
                  {isDeleting ? (
                    <>
                      <Icon name="progress_activity" className="animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="delete" />
                      <span>Delete Account</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
